import React, { useState, useEffect, useContext, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, makeStyles } from '@material-ui/core';
import { ArrowForward } from '@material-ui/icons';
import { colorSet } from '@components/Provider';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import * as Dialog from '@components/Dialog';
import { requestEnumToString } from '@utils/enumToString';
import {
  CapaPcbServiceType,
  CapaQuotationDetailedServiceType,
  CapaQuotationServiceType
} from '@generated/graphql';
import useRequest from '@hooks/useRequest';
import { Enum_Action_Button_Enum } from '@hooks/useUserActionLog';
import useQueryString from '@hooks/useQueryString';
import {
  CapabilityCard,
  CapabilityAccordionCard,
  ProcessmethodCard
} from '@routes/Home/Components/Cards';
import {
  DesignRequestStageEnum,
  PcbRequestStageEnum,
  RequestQueryString
} from '@routes/NewRequest/types';
import { MediaQuery } from '../HomePresenter';
import { capability, processmethodDetails } from '../CapabilityDatas';
import PcbServicetypeCard from './Cards/PcbServicetypeCard';
import { HomeComponentContainer } from './styled';
import ServiceSurvey from '@components/ServiceSurvey/ServiceSurvey';

const Capabilities: React.FC = () => {
  const [
    currentPositionOfSelectedSlide,
    setCurrentPositionOfSelectedSlide
  ] = useState<number>(0);
  const [selectedService, setSelectedService] = useState<
    CapaQuotationServiceType
  >(CapaQuotationServiceType.Cnc);
  const [selectedNumber, setSelectedNumber] = useState<number>(0);
  const [selectedStatus, setSelectedStatus] = useState<boolean>(false);
  const [
    selectedDetailedServiceType,
    setSelectedDetailedServiceType
  ] = useState<CapaQuotationDetailedServiceType | null>(null);
  const [pcbProcess, setPcbProcess] = useState<CapaPcbServiceType[]>([]);
  const [hasRequestDialogOpen, setHasRequestDialogOpen] = useState<boolean>(
    false
  );
  const { md, sm } = useContext(MediaQuery);
  const listRef = useRef<HTMLDivElement>(null);

  const {
    container,
    xs_container,
    upperBox,
    textBox,
    smtextBox,
    mainCarousel,
    bottomContainer
  } = useStyles({
    currentPositionOfSelectedSlide: currentPositionOfSelectedSlide,
    selectedNumber: selectedNumber,
    selectStatus: selectedStatus,
    size: 179 + 16
  });
  const isPcb = selectedService === CapaQuotationServiceType.Pcb;
  const isDesign = selectedService === CapaQuotationServiceType.Design;

  const {
    detailTextBox,
    processmethodBox,
    cardAlignBox,
    xs_capabilities,
    noProcessMethodRequest
  } = useDetailStyle({
    noProcessMethod: isDesign
  });
  const {
    requestState,
    onInitRfqState,
    hasNotEndedRequest,
    lastRequestQuery
  } = useRequest();
  const history = useHistory();
  const { getQueryString } = useQueryString<RequestQueryString>();
  const onOpenBlog = () => {
    return window.open(
      'https://blog.capa.ai/?utm_source=capa_landingpage&utm_medium=contents&utm_campaign=%EA%B0%80%EA%B3%B5%EB%B0%A9%EC%8B%9D%EC%9D%B4_%EA%B6%81%EA%B8%88%ED%95%98%EB%8B%A4%EB%A9%B4_capa_landingpage&utm_id=capa&utm_term=20220216&utm_content=landingpage_text'
    );
  };

  const onSendRequest = (requestInfo: RequestQueryString) => {
    history.push({
      pathname: '/request',
      search: getQueryString({
        serviceType: requestInfo.serviceType,
        process: requestInfo?.process?.toString() ?? undefined,
        stage:
          requestInfo.serviceType === CapaQuotationServiceType.Pcb
            ? PcbRequestStageEnum.PROCESS_SELECT
            : requestInfo.serviceType === CapaQuotationServiceType.Design
            ? DesignRequestStageEnum.PROCESS_SELECT
            : undefined,
        detailedServiceType: requestInfo?.detailedServiceType
      }),
      state: {
        fromLandingPage: true
      }
    });
  };

  const onClickPcbService = (service: CapaPcbServiceType) => {
    setPcbProcess((prev) => {
      if (prev.includes(service)) {
        return prev.filter((serviceType) => serviceType !== service);
      } else {
        return [...prev, service];
      }
    });
  };

  const handleClick = (index: number) => {
    if (index === selectedNumber && selectedStatus) {
      setSelectedStatus(false);
    } else {
      setTimeout(() => setSelectedStatus(true), 150);
    }
  };

  useEffect(() => {
    const handleClose = (e: MouseEvent): void => {
      if (listRef.current && !listRef.current.contains(e.target as Node)) {
        setSelectedStatus(false);
      }
    };
    document.addEventListener('mousedown', handleClose);
    return () => document.removeEventListener('mousedown', handleClose);
  }, [listRef]);

  useEffect(() => {
    if (selectedNumber > 4) {
      setCurrentPositionOfSelectedSlide(selectedNumber % 5);
    } else {
      setCurrentPositionOfSelectedSlide(selectedNumber);
    }
  }, [selectedNumber]);

  useEffect(() => {
    setSelectedService(
      capability[selectedNumber].processmethodKey as CapaQuotationServiceType
    );
  }, [selectedNumber]);

  if (sm) {
    return (
      <HomeComponentContainer padding="100px 16px" className={xs_container}>
        <Dialog.ConfirmPresenter
          isOpen={hasRequestDialogOpen}
          title={`이전에 진행하던 ${requestEnumToString(
            requestState.serviceType
          )} 요청이 남아있습니다.`}
          description="이전에 진행하던 요청을 불러올까요?"
          onOk={() => {
            setHasRequestDialogOpen(false);
            onSendRequest(lastRequestQuery);
          }}
          okText="불러오기"
          onCancle={() => {
            onInitRfqState();
            onSendRequest({
              serviceType: selectedService,
              detailedServiceType: selectedDetailedServiceType ?? undefined,
              process:
                (pcbProcess?.length ?? []) > 0
                  ? pcbProcess.toString()
                  : undefined
            });
            setHasRequestDialogOpen(false);
          }}
          onClose={() => {
            setHasRequestDialogOpen(false);
          }}
          closeText="이전 요청 삭제"
        />

        <CustomizedTypography
          className={smtextBox}
          fontWeight="700"
          color="textPrimary"
        >
          필요한 제조서비스를 <br /> 찾아보세요
        </CustomizedTypography>
        <Box className={xs_capabilities}>
          {capability.map((servicetype) => (
            <CapabilityAccordionCard
              {...servicetype}
              onHasRequestDialogOpen={() => setHasRequestDialogOpen(true)}
              initializePcbServices={() => setPcbProcess([])}
              key={servicetype.label}
              processmethod={
                servicetype.processmethodKey &&
                servicetype.processmethodKey !==
                  CapaQuotationServiceType.Pcb ? (
                  <>
                    {processmethodDetails[
                      servicetype.processmethodKey as CapaQuotationServiceType
                    ].map((method) => (
                      <div>
                        <ProcessmethodCard
                          {...method}
                          key={method.label}
                          onHasRequestDialogOpen={() =>
                            setHasRequestDialogOpen(true)
                          }
                          setDetailedServiceType={(
                            service: CapaQuotationDetailedServiceType
                          ) => setSelectedDetailedServiceType(service)}
                        />
                      </div>
                    ))}
                    <ProcessmethodCard
                      serviceType={servicetype.processmethodKey}
                      detailedServiceType={
                        CapaQuotationDetailedServiceType.Recommend
                      }
                      onHasRequestDialogOpen={() =>
                        setHasRequestDialogOpen(true)
                      }
                      setDetailedServiceType={(
                        service: CapaQuotationDetailedServiceType
                      ) => setSelectedDetailedServiceType(service)}
                      reccomendation
                    />
                  </>
                ) : (
                  <>
                    {processmethodDetails[
                      servicetype.processmethodKey as CapaQuotationServiceType
                    ].map((method) => (
                      <div>
                        <PcbServicetypeCard
                          {...method}
                          key={method.label}
                          isSelected={pcbProcess.includes(
                            method.detailedServiceType as CapaPcbServiceType
                          )}
                          onClick={() => {
                            onClickPcbService(
                              method.detailedServiceType as CapaPcbServiceType
                            );
                          }}
                        />
                      </div>
                    ))}

                    <CustomizedButton
                      color="primary"
                      variant="contained"
                      className="pcb-request-button"
                      size="large"
                      fullWidth
                      onClick={() => {
                        if (hasNotEndedRequest) {
                          setHasRequestDialogOpen(true);
                          return;
                        }

                        if (pcbProcess.length === 0) {
                          history.push({
                            pathname: '/request',
                            search: getQueryString({
                              serviceType: CapaQuotationServiceType.Pcb,
                              process: undefined,
                              stage: PcbRequestStageEnum.PROCESS_SELECT
                            })
                          });
                        } else {
                          onSendRequest({
                            serviceType: CapaQuotationServiceType.Pcb,
                            process: pcbProcess.toString()
                          });
                        }
                      }}
                    >
                      전자회로(PCB) 견적 요청하기
                    </CustomizedButton>
                  </>
                )
              }
            />
          ))}
          <ServiceSurvey>
            <ServiceSurvey.MobileCard bgcolor={colorSet.gray200} />
          </ServiceSurvey>
        </Box>
        <CustomizedButton
          variant="text"
          color="primary"
          size="large"
          endIcon={<ArrowForward fontSize="small" />}
          onClick={onOpenBlog}
          logId={Enum_Action_Button_Enum.Landing_About_Process}
        >
          <CustomizedTypography variant="h4" fontWeight="500" color="primary">
            가공방식이 더 궁금하다면?
          </CustomizedTypography>
        </CustomizedButton>
      </HomeComponentContainer>
    );
  } else {
    return (
      <>
        <HomeComponentContainer
          className={container}
          padding={md ? '120px 36px 140px' : '140px 0px 160px'}
        >
          <Dialog.ConfirmPresenter
            isOpen={hasRequestDialogOpen}
            title={`이전에 진행하던 ${requestEnumToString(
              requestState.serviceType
            )} 요청이 남아있습니다.`}
            description="이전에 진행하던 요청을 불러올까요?"
            onOk={() => {
              setHasRequestDialogOpen(false);
              onSendRequest(lastRequestQuery);
            }}
            okText="불러오기"
            onCancle={() => {
              onInitRfqState();
              onSendRequest({
                serviceType: selectedService,
                detailedServiceType: selectedDetailedServiceType ?? undefined,
                process:
                  (pcbProcess?.length ?? []) > 0
                    ? pcbProcess.toString()
                    : undefined
              });
              setHasRequestDialogOpen(false);
            }}
            onClose={() => {
              setHasRequestDialogOpen(false);
            }}
            closeText="이전 요청 삭제"
          />
          <Box className={upperBox}>
            <Box className={textBox}>
              <CustomizedTypography
                variant="h1"
                fontWeight="700"
                color="textPrimary"
              >
                필요한 제조서비스를 찾아
                <br /> 견적을 요청해보세요
              </CustomizedTypography>
              <CustomizedButton
                variant="text"
                color="primary"
                size="large"
                endIcon={<ArrowForward fontSize="large" />}
                onClick={onOpenBlog}
                logId={Enum_Action_Button_Enum.Landing_About_Process}
              >
                <CustomizedTypography
                  variant="h4"
                  fontWeight="500"
                  color="primary"
                >
                  가공방식이 궁금하다면?
                </CustomizedTypography>
              </CustomizedButton>
            </Box>
          </Box>
          <div ref={listRef}>
            <Box className={mainCarousel}>
              {capability.map((capa, index) => {
                return (
                  <Box
                    key={capa.label}
                    onClick={() => {
                      handleClick(index);
                      setSelectedNumber(index);
                    }}
                  >
                    <CapabilityCard
                      {...capa}
                      isSelected={
                        selectedStatus ? index === selectedNumber : false
                      }
                    />
                  </Box>
                );
              })}
              <ServiceSurvey>
                <ServiceSurvey.Card
                  bgcolor={colorSet.gray200}
                  width={179}
                  height={211}
                />
              </ServiceSurvey>
              {selectedStatus && (
                <Box className={bottomContainer}>
                  <Box className={detailTextBox}>
                    <CustomizedTypography
                      className="detailTitle"
                      variant="h4"
                      fontWeight="700"
                      color="textPrimary"
                      gutterBottoms={isDesign ? 16 : 2}
                    >
                      <b>{capability[selectedNumber].label}</b>{' '}
                      {!isDesign && isPcb
                        ? '작업공정'
                        : isDesign
                        ? ''
                        : '세부 가공방식'}
                    </CustomizedTypography>
                    {!isDesign && (
                      <CustomizedTypography variant="body1" gutterBottoms={24}>
                        {isPcb
                          ? '필요한 작업을 선택하여 견적을 요청해보세요.'
                          : '세부가공방식을 선택하여 견적을 요청해보세요.'}
                      </CustomizedTypography>
                    )}
                  </Box>

                  {!isDesign && !isPcb && (
                    <Box className={processmethodBox}>
                      <Box className={cardAlignBox}>
                        {processmethodDetails[selectedService].map((card) => (
                          <div >
                            <ProcessmethodCard
                              {...card}
                              key={card.label}
                              onHasRequestDialogOpen={() =>
                                setHasRequestDialogOpen(true)
                              }
                              setDetailedServiceType={(
                                service: CapaQuotationDetailedServiceType
                              ) => setSelectedDetailedServiceType(service)}
                            />
                          </div>
                        ))}
                        <ProcessmethodCard
                          serviceType={selectedService}
                          detailedServiceType={
                            CapaQuotationDetailedServiceType.Recommend
                          }
                          reccomendation={true}
                          onHasRequestDialogOpen={() =>
                            setHasRequestDialogOpen(true)
                          }
                          setDetailedServiceType={(
                            service: CapaQuotationDetailedServiceType
                          ) => setSelectedDetailedServiceType(service)}
                        />
                      </Box>
                    </Box>
                  )}

                  {isPcb && (
                    <Box className={processmethodBox}>
                      <CustomizedTypography
                        variant="body1"
                        color="textSecondary"
                        gutterBottoms={12}
                      >
                        (중복선택 가능)
                      </CustomizedTypography>
                      <Box className={cardAlignBox}>
                        {processmethodDetails[selectedService].map((card) => {
                          const { english, ...rest } = card;
                          return (

                              <PcbServicetypeCard
                                {...rest}
                                key={card.label}
                                onClick={() =>
                                  onClickPcbService(
                                    card.detailedServiceType as CapaPcbServiceType
                                  )
                                }
                                isSelected={pcbProcess.includes(
                                  card.detailedServiceType as CapaPcbServiceType
                                )}
                              />
                            
                          );
                        })}
                      </Box>
                      <CustomizedButton
                        color="primary"
                        variant="contained"
                        className="pcb-request-button"
                        size="large"
                        onClick={() => {
                          if (hasNotEndedRequest) {
                            setHasRequestDialogOpen(true);
                            return;
                          }

                          if (pcbProcess.length === 0) {
                            history.push({
                              pathname: '/request',
                              search: getQueryString({
                                serviceType: CapaQuotationServiceType.Pcb,
                                process: undefined,
                                stage: PcbRequestStageEnum.PROCESS_SELECT
                              })
                            });
                          } else {
                            onSendRequest({
                              serviceType: CapaQuotationServiceType.Pcb,
                              process: pcbProcess.toString()
                            });
                          }
                        }}
                      >
                        전자회로(PCB) 견적 요청하기
                      </CustomizedButton>
                    </Box>
                  )}

                  {isDesign && !isPcb && (
                    <Box className={processmethodBox}>
                      <CustomizedTypography
                        variant="body1"
                        color="textPrimary"
                        gutterBottoms={24}
                        className="design-content"
                      >
                        제품 외형에 대해 아이디어 스케치를 바탕을 3D 모델링
                        제작하는 <b>제품디자인</b>
                        <br />
                        부터, 제품 디자인이 실현될 수 있게 제조성을 고려한
                        설계를 하는 <b>기구설계</b>와 실제양산을 위한{' '}
                        <b>시제품 제작</b>까지 요청할 수 있는 서비스로, 도면없이
                        참고이미지와 설명으로도 견적요청이 가능해요.
                      </CustomizedTypography>
                      <CustomizedButton
                        variant="contained"
                        color="primary"
                        size="large"
                        className={noProcessMethodRequest}
                        onClick={() => {
                          if (hasNotEndedRequest) {
                            setHasRequestDialogOpen(true);
                          } else {
                            onSendRequest({
                              serviceType: selectedService
                            });
                          }
                        }}
                      >
                        <CustomizedTypography variant="body1" fontWeight="500">
                          디자인/설계 견적 요청하기
                        </CustomizedTypography>
                      </CustomizedButton>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </div>
        </HomeComponentContainer>
      </>
    );
  }
};

export default Capabilities;

const useStyles = makeStyles(() => ({
  container: {
    backgroundColor: colorSet.gray100
  },
  xs_container: {
    backgroundColor: colorSet.gray100
  },
  upperBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '& button': {
      marginTop: 'auto'
    }
  },
  textBox: {
    display: 'flex',
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 58,
    '& button': {
      marginBottom: 8
    }
  },
  smtextBox: {
    fontSize: '28px',
    marginRight: 'auto',
    marginBottom: 40
  },
  mainCarousel: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    width: 'calc(100% + 2px)',
    position: 'relative'
  },
  bottomContainer: (props: {
    currentPositionOfSelectedSlide: number;
    selectStatus: boolean;
    selectedNumber: number;
    size: number;
  }) => ({
    display: 'block',
    minWidth: 538,
    width: 538,
    padding: '24px 24px 32px 24px',
    borderRadius: 16,
    position: 'absolute',
    boxShadow: '0px 24px 56px 4px rgba(0, 0, 0, 0.04)',
    top: props.selectedNumber <= 4 ? 211 + 16 : 422 + 16 + 16,
    zIndex: 99,
    backgroundColor: colorSet.gray000,
    left: `${
      props.currentPositionOfSelectedSlide < 3
        ? props.currentPositionOfSelectedSlide * props.size + 'px'
        : 'auto'
    }`,
    right: `${
      props.currentPositionOfSelectedSlide > 2
        ? (4 - props.currentPositionOfSelectedSlide) * props.size + 'px'
        : 'auto'
    }`
  })
}));

const useDetailStyle = makeStyles((theme) => ({
  detailTextBox: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginRight: 'auto',
    '& > button:hover': {
      backgroundColor: `${colorSet.gray200} !important`
    },
    '& p': {
      color: colorSet.gray700
    }
  },
  processmethodBox: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    maxWidth: 880,
    '& .pcb-request-button': {
      marginTop: 40
    },
    '& .design-content': {
      wordBreak: 'break-all',
      letterSpacing: '-0.2px'
    }
  },
  cardAlignBox: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    '& .reccomendation': {
      width: 304,
      height: 139,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 16,
      boxShadow: 'unset',
      padding: '0',
      backgroundColor: colorSet.gray300,
      transition: '0.3s',
      '&:hover': {
        backgroundColor: colorSet.gray400,
        cursor: 'pointer'
      },
      '& .MuiCardContent-root': {
        width: '100%',
        height: '100%',
        padding: 0,
        '& button': {
          height: '100%',
          '& svg': {
            fill: colorSet.gray700
          },
          '&:hover': {
            backgroundColor: colorSet.gray400,
            cursor: 'pointer',
            '& svg': {
              fill: colorSet.primary500
            }
          }
        }
      },
      '& .reccomendationText': {
        marginRight: 60,
        textAlign: 'left'
      }
    }
  },
  xs_capabilities: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    marginBottom: 60
  },
  noProcessMethodRequest: {
    width: '222px !important',
    height: '48px !important',
    '& p': {
      color: colorSet.gray000
    }
  }
}));
